import { useIntl } from 'react-intl';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { Form } from '~/eds/src/modules/Form/Form';
import { StateUpdater } from '~/eds/src/modules/Form/types';
import {
  requiredValidator,
  uuidValidator,
} from '~/eds/src/modules/Form/validators';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';

import { ClientSelect } from './ClientSelect';

type CopyWorkflowFormState = {
  sourceWorkflowId: string;
  destinationEnvironment: string;
  destinationWorkflowId: string;
};

const initialState = {
  sourceWorkflowId: { value: '' },
  destinationEnvironment: { value: '' },
  destinationWorkflowId: { value: '' },
};

const CopyWorkflowPage = () => {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const nav = generateNav({
    current: {
      text: intl.formatMessage({
        id: 'CopyWorkflowPage.pageTitle',
        defaultMessage: 'Copy Workflow',
        description: 'Title for the copy workflow page',
      }),
      pathname: RoutePathType.AdminConsoleClientCopyWorkflow,
    },
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId: currentUser.client,
    },
    intl,
  });
  const { toast } = useToast();

  const [copyWorkflow, { isLoading: isCopying }] =
    api.endpoints.copyWorkflow.useMutation();

  const handleOnSubmit = async (
    formState: StateUpdater<CopyWorkflowFormState>,
  ) => {
    const { sourceWorkflowId, destinationEnvironment, destinationWorkflowId } =
      formState.state;

    try {
      await copyWorkflow({
        sourceWorkflowId: sourceWorkflowId.value,
        destinationClientId: Number(destinationEnvironment.value),
        destinationWorkflowId: destinationWorkflowId.value,
      }).unwrap();
      toast({
        message: intl.formatMessage({
          id: 'CopyWorkflowPage.workflowCopiedSuccess',
          defaultMessage: 'Workflow copied successfully.',
          description: 'Toast message for successful workflow copy',
        }),
        status: 'success',
      });
    } catch (error: any) {
      const detail =
        error?.response?.data?.errors?.[0]?.detail ??
        error?.response?.data?.data ??
        '';
      toast({
        message: intl.formatMessage(
          {
            id: 'CopyWorkflowPage.workflowCopyError',
            defaultMessage:
              'There was an error when copying the workflow. {detail}',
            description:
              'Toast message for workflow copy error. detail is the error detail string. Example: detail = "Invalid workflow ID"',
          },
          { detail },
        ),
        status: 'danger',
      });
    }
  };

  const fields = {
    sourceWorkflowId: {
      order: 1,
      key: 'sourceWorkflowId',
      type: 'field' as const,
      name: 'sourceWorkflowId',
      label: intl.formatMessage({
        id: 'CopyWorkflowPage.sourceWorkflowIdLabel',
        defaultMessage: 'Source Workflow ID',
        description: 'Label for source workflow ID field',
      }),
      input: TextInput,
      placeholder: intl.formatMessage({
        id: 'CopyWorkflowPage.sourceWorkflowIdPlaceholder',
        defaultMessage: 'e.g 00000000-0000-0000-0000-000000000000',
        description: 'Placeholder for source workflow ID field',
      }),
      required: true,
    },
    destinationEnvironment: {
      order: 2,
      key: 'destinationEnvironment',
      type: 'field' as const,
      name: 'destinationEnvironment',
      label: intl.formatMessage({
        id: 'CopyWorkflowPage.destinationEnvironmentLabel',
        defaultMessage: 'Destination Environment',
        description: 'Label for destination environment field',
      }),
      input: ClientSelect,
      required: true,
    },
    destinationWorkflowId: {
      order: 3,
      key: 'destinationWorkflowId',
      type: 'field' as const,
      name: 'destinationWorkflowId',
      label: intl.formatMessage({
        id: 'CopyWorkflowPage.destinationWorkflowIdLabel',
        defaultMessage: 'Destination Workflow ID',
        description: 'Label for destination workflow ID field',
      }),
      input: TextInput,
      placeholder: intl.formatMessage({
        id: 'CopyWorkflowPage.destinationWorkflowIdPlaceholder',
        defaultMessage: 'e.g 00000000-0000-0000-0000-000000000000',
        description: 'Placeholder for destination workflow ID field',
      }),
      required: false,
    },
  };

  return (
    <PageLayout
      nav={nav}
      title={intl.formatMessage({
        id: 'CopyWorkflowPage.pageTitle',
        defaultMessage: 'Copy Workflow',
        description: 'Title for the copy workflow page',
      })}
      description={intl.formatMessage({
        id: 'CopyWorkflowPage.pageDescription',
        defaultMessage: 'Copy a workflow from one client to another.',
        description: 'Description for the copy workflow page',
      })}
    >
      <Layout spacing={4} direction="column" w="500px">
        <Form<CopyWorkflowFormState>
          fields={fields}
          state={initialState}
          onSubmit={handleOnSubmit}
          submitButtonProps={{
            text: intl.formatMessage({
              id: 'CopyWorkflowPage.copyWorkflowButton',
              defaultMessage: 'Copy Workflow',
              description: 'Button text for copying workflow',
            }),
            variant: 'primary',
            isLoading: isCopying,
          }}
          validators={[
            requiredValidator(
              ['sourceWorkflowId', 'destinationEnvironment'],
              intl,
            ),
            uuidValidator(['sourceWorkflowId', 'destinationWorkflowId'], intl),
          ]}
        />
      </Layout>
    </PageLayout>
  );
};

export default CopyWorkflowPage;
